import { PlusOutlined } from '@ant-design/icons';
import {
  Button,
  Col,
  Form,
  Input,
  message,
  Modal,
  Row,
  Typography,
  Upload,
} from 'antd';
import type { UploadFile } from 'antd/lib/upload';
import React, { useState } from 'react';

import { Instructor } from '@/types/database/instructor';

import { patchInstructor, postInstructor } from '../../helpers/api';
import { getInitialValues } from './helpers';
import styles from './styles.module.scss';

const { Item, useForm } = Form;

type FormValues = Omit<
  Instructor,
  '_id' | 'image' | 'createdTime' | 'updatedTime'
> & {
  image?: UploadFile[];
};

const EditInstructorButton: React.FC<{
  buttonLabel: string;
  instructor?: Instructor;
  onConfirm: () => void;
}> = ({ instructor, onConfirm, buttonLabel }) => {
  const [open, setOpen] = useState(false);
  const [form] = useForm<FormValues>();
  const [imageUrl, setImageUrl] = useState<string | null>(
    instructor?.image || null
  );

  const type = instructor ? 'update' : 'create';

  const initialValues = getInitialValues(instructor);

  const reset = () => {
    form.resetFields();
    setImageUrl(instructor?.image || null);
  };

  const handleFinish = async (values: FormValues) => {
    enum TypeMessages {
      update = '修改',
      create = '新增',
    }

    const formData = new FormData();

    Object.entries(values).forEach(([key, value]) => {
      if (!value) return;
      if (Array.isArray(value)) {
        const originFileObj = value[0]?.originFileObj;
        if (originFileObj) {
          formData.append(key, originFileObj);
        }
      } else {
        formData.append(key, value);
      }
    });

    try {
      if (instructor) {
        await patchInstructor(instructor._id, formData);
      } else {
        await postInstructor(formData);
      }
      message.success(`${TypeMessages[type]}成功`);
      onConfirm();
      if (type === 'create') {
        reset();
      }
      setOpen(false);
    } catch (error) {
      message.error(`${TypeMessages[type]}失敗`);
      return;
    }
  };

  return (
    <>
      <Button onClick={() => setOpen(true)}>{buttonLabel}</Button>
      <Modal
        title="講師資訊"
        open={open}
        closable
        onCancel={() => {
          reset();
          setOpen(false);
        }}
        cancelButtonProps={{ hidden: true }}
        onOk={form.submit}
        okText="儲存"
        okButtonProps={{ type: 'default' }}
        width={1242}
      >
        <Form
          form={form}
          onFinish={handleFinish}
          labelCol={{ span: 24 }}
          initialValues={initialValues}
        >
          <Row>
            <Col span={12} className={styles['form-col']}>
              <Item
                label="講師姓名"
                name="name"
                required
                rules={[{ required: true, message: '請輸入講師姓名' }]}
              >
                <Input placeholder="例如：王小明" />
              </Item>
              <Item
                label="手機號碼"
                name="phone"
                required
                rules={[
                  { required: true, message: '請輸入手機號碼' },
                  { pattern: /^09\d{8}$/, message: '手機號碼格式錯誤' },
                ]}
              >
                <Input placeholder="例如：0987654321" />
              </Item>
              <Item
                label="頭銜"
                name="title"
                required
                rules={[{ required: true, message: '請輸入頭銜' }]}
              >
                <Input placeholder="例如：廣播&口語表達大師" />
              </Item>
              <Item
                label="上傳圖片"
                name="image"
                valuePropName="fileList"
                getValueFromEvent={(e) => {
                  const { file, fileList } = e;
                  console.log({ file, fileList });
                  setImageUrl(URL.createObjectURL(file));
                  return fileList;
                }}
              >
                <Upload
                  accept="image/*"
                  beforeUpload={() => false}
                  listType="picture-card"
                  maxCount={1}
                  showUploadList={false}
                  className={styles['upload']}
                >
                  {!imageUrl && (
                    <div className={styles['upload-div']}>
                      <div>
                        <PlusOutlined />
                      </div>
                      <Typography>Upload</Typography>
                    </div>
                  )}
                  {imageUrl && (
                    <img
                      src={imageUrl}
                      alt="avatar"
                      className={styles['preview-image']}
                    />
                  )}
                </Upload>
              </Item>
            </Col>
            <Col span={12} className={styles['form-col']}>
              <Item label="專長" name="expertise">
                <Input.TextArea
                  maxLength={100}
                  showCount
                  autoSize={{ maxRows: 4, minRows: 4 }}
                  placeholder="例如：網路通訊/物聯網產品設計/AI語音處理/資訊軟體設計..."
                />
              </Item>
              <Item label="簡介" name="introduction">
                <Input.TextArea
                  maxLength={100}
                  showCount
                  autoSize={{ maxRows: 4, minRows: 4 }}
                  placeholder="例如：身為資深講師的王小明老師，分享知識時永遠把焦點..."
                />
              </Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

export default EditInstructorButton;
